import {FC, ReactNode, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {
  Controller,
  FormProvider,
  Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useUpdateEffect from 'ahooks/es/useUpdateEffect';
import {isAxiosError} from 'axios';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {useSnackbar} from 'notistack';
import {mixed, object, string} from 'yup';

import {
  Category,
  CategorySelect,
  SubCategory,
  SubCategorySelect,
} from '@entities/category';
import {
  GeoFilterOption,
  useGetGeoFilterOptions,
  useUploadAdminFile,
} from '@entities/common';
import {
  Event,
  EventDisplayType,
  EventPrice,
  EventStatus,
  formatEventDisplayType,
  formatParticipantLogoOption,
  getEventPrice,
  ParticipantLogoOption,
  TopicSelect,
  useUpdateEvent,
} from '@entities/event';
import {Topic} from '@entities/topic';
import {getBackendAssetPath, getErrorMessageFromApiError} from '@shared/api';
import {
  FieldChangeHighlight as FieldHighlight,
  imageFileTypes,
  ImageUploadField,
  ObjectSelect,
  RhfDateTimePicker,
  TranslateFieldButton,
} from '@shared/components';
import {
  DATETIME_FORMAT,
  ROUTE__CATEGORY_EDIT,
  ROUTE__SUB_CATEGORY_EDIT,
  ROUTE__TOPIC_DETAILS,
} from '@shared/constants';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {
  dummyFunc,
  formatInTz,
  numberTransformEmptyStringToNull,
  toPickerDateInTz,
  toUtcFromTz,
} from '@shared/utils';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';
import {FormRow} from '@widgets/common/FormRow';

// "Topic" → use the topic's logo, so we send an empty logo_url.
// "Unique" → send the user-uploaded logo_url.
const LOGO_TYPE_CHOICES: ParticipantLogoOption[] = Object.values(
  ParticipantLogoOption,
);

const v8nSchema = object().shape({
  name: string().required(),
  shortName: string().required(),
  description: string().defined(),
  yesText: string().defined().default(''),
  noText: string().defined().default(''),
  overview: string().defined().default(''),
  sourceOfTruth: string().defined().default(''),
  rules: string().defined().default(''),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>().nullable().defined(),
  topic: mixed<Topic>().nullable().defined(),
  geos: mixed<GeoFilterOption[]>().defined().default([]),
  startDatetime: mixed<Date>().required(),
  endDatetime: mixed<Date>().required(),
  liveDatetime: mixed<Date>().nullable().defined(),
  autoPublishEnabled: mixed<boolean>().defined(),
  yesPrice: numberTransformEmptyStringToNull
    .required()
    .positive('must be positive')
    .max(0.99, 'max 0.99'),
  displayType: string().oneOf(Object.values(EventDisplayType)).required(),
  isPromoted: mixed<boolean>().defined().default(false),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  shortLogoFile: mixed<FileWithPath>().nullable().defined(),
  participantLogoOption: mixed<ParticipantLogoOption>().nullable().defined(),
  participantShortLogoOption: mixed<ParticipantLogoOption>()
    .nullable()
    .defined(),
});

type FormValues = {
  name: string;
  shortName: string;
  description: string;
  yesText: string;
  noText: string;
  overview: string;
  sourceOfTruth: string;
  rules: string;
  category: Category | null;
  subCategory: SubCategory | null;
  topic: Topic | null;
  geos: GeoFilterOption[];
  startDatetime: Date | null;
  endDatetime: Date | null;
  liveDatetime: Date | null;
  autoPublishEnabled: boolean;
  yesPrice: number;
  displayType: EventDisplayType;
  isPromoted: boolean;
  logoFile: FileWithPath | null;
  shortLogoFile: FileWithPath | null;
  participantLogoOption: ParticipantLogoOption | null;
  participantShortLogoOption: ParticipantLogoOption | null;
};

const SectionTitle: FC<{children: ReactNode}> = ({children}) => (
  <Typography variant="h3bold" display="block" mb={4} mt={1}>
    {children}
  </Typography>
);

interface Props {
  event: Event;
  onClose: () => void;
}

export const EditEventForm: FC<Props> = ({event, onClose}) => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {data: geoOptions = []} = useGetGeoFilterOptions();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();
  const {mutateAsync: updateEvent, isPending: isUpdating} = useUpdateEvent();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => event.nameTranslations ?? createEmptyTranslationDict(),
  );
  const [shortNameTranslations, setShortNameTranslations] =
    useState<TranslationDict>(
      () => event.shortNameTranslations ?? createEmptyTranslationDict(),
    );
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationDict>(
      () => event.descriptionTranslations ?? createEmptyTranslationDict(),
    );
  const [yesTextTranslations, setYesTextTranslations] =
    useState<TranslationDict>(
      () => event.yesTextTranslations ?? createEmptyTranslationDict(),
    );
  const [noTextTranslations, setNoTextTranslations] = useState<TranslationDict>(
    () => event.noTextTranslations ?? createEmptyTranslationDict(),
  );
  const [overviewTranslations, setOverviewTranslations] =
    useState<TranslationDict>(
      () => event.overviewTranslations ?? createEmptyTranslationDict(),
    );
  const [sourceOfTruthTranslations, setSourceOfTruthTranslations] =
    useState<TranslationDict>(
      () => event.sourceOfTruthTranslations ?? createEmptyTranslationDict(),
    );
  const [rulesTranslations, setRulesTranslations] = useState<TranslationDict>(
    () => event.rulesTranslations ?? createEmptyTranslationDict(),
  );

  // Whether the user cleared the already-saved short logo image. When true and
  // no new file is picked, short_logo_url is sent as null on submit.
  const [isShortLogoCleared, setIsShortLogoCleared] = useState(false);

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema) as unknown as Resolver<FormValues>,
    defaultValues: {
      name: event.name,
      shortName: event.shortName ?? '',
      description: event.description,
      yesText: event.yesText ?? '',
      noText: event.noText ?? '',
      overview: event.overview ?? '',
      sourceOfTruth: event.sourceOfTruth ?? '',
      rules: event.rules ?? '',
      category: event.category,
      subCategory: event.subCategories[0] ?? null,
      topic: event.topic
        ? ({
            id: event.topic.id,
            topicId: event.topic.topicId,
            name: event.topic.name,
            logoUrl: event.topic.logoUrl ?? '',
          } as Topic)
        : null,
      // The saved tags are known by slug only; the label of each chip is
      // resolved from geoOptions on render, which may still be loading.
      geos: event.geoSlugs.map(slug => ({id: slug, name: slug})),
      startDatetime: toPickerDateInTz(event.startDate),
      endDatetime: toPickerDateInTz(event.endDate),
      liveDatetime: event.liveDate ? toPickerDateInTz(event.liveDate) : null,
      autoPublishEnabled: event.autoPublishEnabled,
      // event.yesPrice is the backend yes_price / 10000; the field shows that
      // price fraction (0–0.99) directly and is sent back ×10000.
      yesPrice: Math.round(event.yesPrice * 10_000) / 10_000,
      displayType: event.displayType,
      isPromoted: event.isPromoted ?? false,
      logoFile: null,
      shortLogoFile: null,
      // Prefill from participant_logo_option (GET); absence ⇒ Topic.
      participantLogoOption:
        event.participantLogoOption ?? ParticipantLogoOption.topic,
      // Prefill from participant_short_logo_option (GET); absence ⇒ Unique,
      // matching the backend's own default for this field.
      participantShortLogoOption:
        event.participantShortLogoOption ?? ParticipantLogoOption.unique,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: {dirtyFields},
  } = formMethods;

  const currentCategory = useWatch({control, name: 'category'});
  const currentSubCategory = useWatch({control, name: 'subCategory'});
  const selectedTopic = useWatch({control, name: 'topic'});
  const startDatetime = useWatch({control, name: 'startDatetime'});
  const participantLogoOption = useWatch({
    control,
    name: 'participantLogoOption',
  });
  const participantShortLogoOption = useWatch({
    control,
    name: 'participantShortLogoOption',
  });
  const yesPrice = useWatch({control, name: 'yesPrice'});
  const noPrice = isNil(yesPrice) ? null : Math.max(0, 1 - Number(yesPrice));

  // "Update price" dialog: suggested price fetched from the football backend.
  const [isPriceDialogOpen, setIsPriceDialogOpen] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [priceData, setPriceData] = useState<EventPrice | null>(null);
  const [suggestedPrice, setSuggestedPrice] = useState('');

  useUpdateEffect(() => {
    if (!isNil(currentCategory)) {
      setValue('subCategory', null, {shouldDirty: true});
    }
  }, [currentCategory?.id]);

  useUpdateEffect(() => {
    setValue('topic', null, {shouldDirty: true});
  }, [currentSubCategory?.id]);

  // Shield/Shield+stroke/Player/Custom are pre-rendered by the backend for
  // the event's participant — pick the matching image so the preview updates
  // the moment the logo type is switched, before saving.
  const participantLogoUrl = (option: ParticipantLogoOption | null): string => {
    switch (option) {
      case ParticipantLogoOption.default: {
        return event.participant?.logoUrl ?? '';
      }
      case ParticipantLogoOption.defaultStroke: {
        return event.participant?.logoUrlStroke ?? '';
      }
      case ParticipantLogoOption.player: {
        return event.participant?.logoUrlPlayer ?? '';
      }
      case ParticipantLogoOption.custom: {
        return event.participant?.logoUrlCustom ?? '';
      }
      default: {
        return '';
      }
    }
  };

  // "Topic" logo type ⇒ preview the topic's own logo (from `selectedTopic`,
  // which carries it both on initial load and once the user picks a
  // different topic); fall back to the event's already backend-resolved
  // image if it's somehow missing. "Unique" ⇒ the event's own uploaded
  // image. Everything else ⇒ the matching participant image.
  const iconPreviewUrl =
    participantLogoOption === ParticipantLogoOption.topic
      ? selectedTopic?.logoUrl || event.logoUrl || ''
      : participantLogoOption === ParticipantLogoOption.unique
        ? event.logoUrl || ''
        : participantLogoUrl(participantLogoOption);
  const shortIconPreviewUrl =
    participantShortLogoOption === ParticipantLogoOption.topic
      ? selectedTopic?.logoUrl || event.shortLogoUrl || ''
      : participantShortLogoOption === ParticipantLogoOption.unique
        ? isShortLogoCleared
          ? ''
          : event.shortLogoUrl || ''
        : participantLogoUrl(participantShortLogoOption);

  // Highlight a field with a blue frame while its value differs from what
  // was originally loaded, so unsaved changes are visible before saving.
  const isIconChanged =
    !!dirtyFields.logoFile || !!dirtyFields.participantLogoOption;
  const isShortIconChanged =
    !!dirtyFields.shortLogoFile ||
    !!dirtyFields.participantShortLogoOption ||
    isShortLogoCleared;

  // A translatable field counts as changed once its English text is dirty,
  // or any other locale was edited via the translate dialog — in which case
  // the English text (and so `dirtyFields`) may be untouched.
  const isTranslatedFieldChanged = (
    fieldName: keyof FormValues,
    translations: TranslationDict,
    originalTranslations: TranslationDict | undefined,
  ): boolean =>
    !!dirtyFields[fieldName] ||
    !isEqual(
      translations,
      originalTranslations ?? createEmptyTranslationDict(),
    );

  const isNameChanged = isTranslatedFieldChanged(
    'name',
    nameTranslations,
    event.nameTranslations,
  );
  const isShortNameChanged = isTranslatedFieldChanged(
    'shortName',
    shortNameTranslations,
    event.shortNameTranslations,
  );
  const isDescriptionChanged = isTranslatedFieldChanged(
    'description',
    descriptionTranslations,
    event.descriptionTranslations,
  );
  const isYesTextChanged = isTranslatedFieldChanged(
    'yesText',
    yesTextTranslations,
    event.yesTextTranslations,
  );
  const isNoTextChanged = isTranslatedFieldChanged(
    'noText',
    noTextTranslations,
    event.noTextTranslations,
  );
  const isOverviewChanged = isTranslatedFieldChanged(
    'overview',
    overviewTranslations,
    event.overviewTranslations,
  );
  const isSourceOfTruthChanged = isTranslatedFieldChanged(
    'sourceOfTruth',
    sourceOfTruthTranslations,
    event.sourceOfTruthTranslations,
  );
  const isRulesChanged = isTranslatedFieldChanged(
    'rules',
    rulesTranslations,
    event.rulesTranslations,
  );

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const isTopicLogo =
        values.participantLogoOption === ParticipantLogoOption.topic;
      const isTopicShortLogo =
        values.participantShortLogoOption === ParticipantLogoOption.topic;

      let logoUrl = getBackendAssetPath(event.logoUrl) ?? '';
      if (values.logoFile) {
        const uploaded = await uploadAdminFile({
          file: values.logoFile,
          type: 'event_logo',
        });
        logoUrl = uploaded.key;
      }
      // Topic logo type → send an empty logo_url.
      if (isTopicLogo) {
        logoUrl = '';
      }

      let shortLogoUrl: string | null;
      if (values.shortLogoFile) {
        const uploaded = await uploadAdminFile({
          file: values.shortLogoFile,
          type: 'event_logo',
        });
        shortLogoUrl = uploaded.key;
      } else if (isShortLogoCleared) {
        shortLogoUrl = null;
      } else {
        shortLogoUrl = getBackendAssetPath(event.shortLogoUrl) ?? '';
      }
      // Topic logo type → send an empty short_logo_url.
      if (isTopicShortLogo) {
        shortLogoUrl = '';
      }

      await updateEvent({
        eventSlug: event.eventId,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        shortNameTrans: {
          ...shortNameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            shortNameTranslations,
            values.shortName,
            !!dirtyFields.shortName,
          ),
        },
        descriptionTrans: {
          ...descriptionTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            descriptionTranslations,
            values.description,
            !!dirtyFields.description,
          ),
        },
        yesTextTrans: {
          ...yesTextTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            yesTextTranslations,
            values.yesText,
            !!dirtyFields.yesText,
          ),
        },
        noTextTrans: {
          ...noTextTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            noTextTranslations,
            values.noText,
            !!dirtyFields.noText,
          ),
        },
        overviewTrans: values.overview
          ? {
              ...overviewTranslations,
              [SupportedLanguage.English]: resolveEnglishText(
                overviewTranslations,
                values.overview,
                !!dirtyFields.overview,
              ),
            }
          : undefined,
        sourceOfTruthTrans: values.sourceOfTruth
          ? {
              ...sourceOfTruthTranslations,
              [SupportedLanguage.English]: resolveEnglishText(
                sourceOfTruthTranslations,
                values.sourceOfTruth,
                !!dirtyFields.sourceOfTruth,
              ),
            }
          : undefined,
        rulesTrans: values.rules
          ? {
              ...rulesTranslations,
              [SupportedLanguage.English]: resolveEnglishText(
                rulesTranslations,
                values.rules,
                !!dirtyFields.rules,
              ),
            }
          : undefined,
        displayType: values.displayType,
        isPromoted: values.isPromoted,
        subCategorySlugs: [
          values.subCategory?.slug ?? values.subCategory?.id ?? '',
        ],
        startDate: values.startDatetime ?? toPickerDateInTz(event.startDate),
        endDate: values.endDatetime ?? toPickerDateInTz(event.endDate),
        liveDate: values.liveDatetime,
        logoUrl,
        shortLogoUrl,
        yesPrice: values.yesPrice,
        noPrice: noPrice ?? 0,
        topicSlug: values.topic?.topicId ?? '',
        participantLogoOption: values.participantLogoOption,
        participantShortLogoOption: values.participantShortLogoOption,
        autoPublishEnabled: values.autoPublishEnabled,
        geoSlugs: values.geos.map(g => g.id),
      });

      enqueueSnackbar('Event updated successfully', {variant: 'success'});
    } catch (error) {
      const msg = isAxiosError(error) ? getErrorMessageFromApiError(error) : '';
      enqueueSnackbar(`Could not update event. ${msg}`, {variant: 'error'});
    }
  };

  const onInvalid = () => {
    enqueueSnackbar('Please fill in all required fields', {variant: 'error'});
  };

  const handleOpenPriceDialog = async () => {
    setIsFetchingPrice(true);
    try {
      const price = await getEventPrice(event.id);
      setPriceData(price);
      // yes_start_ratio is the raw backend ratio (×10000); show it in the same
      // fraction scale as the Yes price field (e.g. 800 → 0.08).
      setSuggestedPrice(String(price.yesStartRatio / 10_000));
      setIsPriceDialogOpen(true);
    } catch (error) {
      const msg = isAxiosError(error) ? getErrorMessageFromApiError(error) : '';
      enqueueSnackbar(`Could not fetch price. ${msg}`, {variant: 'error'});
    } finally {
      setIsFetchingPrice(false);
    }
  };

  // suggestedPrice is already the fraction (same scale as the Yes price field),
  // so apply it directly, then submit a full PUT of the event with the new price.
  const handleUpdatePrice = () => {
    const price = Number(suggestedPrice);
    if (!Number.isNaN(price)) {
      setValue('yesPrice', price, {shouldDirty: true});
    }
    setIsPriceDialogOpen(false);
    void handleSubmit(onSubmit, onInvalid)();
  };

  const translatedField = (
    fieldName: keyof FormValues,
    label: string,
    currentValue: string,
    translations: TranslationDict,
    setTranslations: (t: TranslationDict) => void,
    changed: boolean,
    multiline?: boolean,
  ) => (
    <Box display="flex" alignItems="flex-start" gap={1}>
      <FieldHighlight changed={changed} flex={1}>
        <Controller
          name={fieldName}
          control={control}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              label={label}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              multiline={multiline}
              rows={multiline ? 4 : undefined}
              size="small"
              fullWidth
            />
          )}
        />
      </FieldHighlight>
      <TranslateFieldButton
        onClick={() => {
          void openAddTranslationsDialog({
            fieldName: label,
            initialTranslations: translations,
            enTrans: resolveEnglishText(
              translations,
              currentValue,
              !!dirtyFields[fieldName],
            ),
            isMultiLineText: multiline,
          })
            .then((t: TranslationDict) => {
              setValue(fieldName, t[SupportedLanguage.English] as never, {
                shouldDirty: true,
              });
              setTranslations(t);
              return;
            })
            .catch(dummyFunc);
        }}
      />
    </Box>
  );

  const currentName = watch('name');
  const currentShortName = watch('shortName');
  const currentDescription = watch('description');
  const currentYesText = watch('yesText');
  const currentNoText = watch('noText');
  const currentOverview = watch('overview');
  const currentSourceOfTruth = watch('sourceOfTruth');
  const currentRules = watch('rules');

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        {/* ── INFORMATION ── */}
        <SectionTitle>Information</SectionTitle>
        <Box display="flex" gap={3} alignItems="flex-start">
          {/* Icon */}
          <Box flexShrink={0} width={160}>
            <ImageUploadField<FormValues>
              fieldName="logoFile"
              label="Icon"
              acceptedFileTypes={imageFileTypes}
              existingFileUrl={iconPreviewUrl || undefined}
              width={160}
              height={160}
              alt="Event icon"
              highlighted={isIconChanged}
            />
            <Box mt={4}>
              <FieldHighlight changed={!!dirtyFields.participantLogoOption}>
                <Controller
                  name="participantLogoOption"
                  control={control}
                  render={({field}) => (
                    <ObjectSelect<{value: ParticipantLogoOption; label: string}>
                      label="Logo type"
                      options={LOGO_TYPE_CHOICES.map(o => ({
                        value: o,
                        label: formatParticipantLogoOption(o),
                      }))}
                      labelProp="label"
                      valueProp="value"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: formatParticipantLogoOption(field.value),
                            }
                          : null
                      }
                      onChange={opt => {
                        if (!Array.isArray(opt) && opt !== null) {
                          field.onChange(opt.value);
                        }
                      }}
                      fullWidth
                    />
                  )}
                />
              </FieldHighlight>
            </Box>
            <Box mt={4}>
              <ImageUploadField<FormValues>
                fieldName="shortLogoFile"
                label="Short logo"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={shortIconPreviewUrl || undefined}
                width={160}
                height={160}
                alt="Event short logo"
                onClearExisting={() => setIsShortLogoCleared(true)}
                highlighted={isShortIconChanged}
              />
            </Box>
            <Box mt={4}>
              <FieldHighlight
                changed={!!dirtyFields.participantShortLogoOption}
              >
                <Controller
                  name="participantShortLogoOption"
                  control={control}
                  render={({field}) => (
                    <ObjectSelect<{value: ParticipantLogoOption; label: string}>
                      label="Short logo type"
                      options={LOGO_TYPE_CHOICES.map(o => ({
                        value: o,
                        label: formatParticipantLogoOption(o),
                      }))}
                      labelProp="label"
                      valueProp="value"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: formatParticipantLogoOption(field.value),
                            }
                          : null
                      }
                      onChange={opt => {
                        if (!Array.isArray(opt) && opt !== null) {
                          field.onChange(opt.value);
                        }
                      }}
                      fullWidth
                    />
                  )}
                />
              </FieldHighlight>
            </Box>
          </Box>

          {/* Info fields */}
          <Stack spacing={3.5} flex={1}>
            {translatedField(
              'name',
              'Name',
              currentName,
              nameTranslations,
              setNameTranslations,
              isNameChanged,
            )}
            {translatedField(
              'shortName',
              'Short name',
              currentShortName,
              shortNameTranslations,
              setShortNameTranslations,
              isShortNameChanged,
            )}
            {translatedField(
              'description',
              'Description',
              currentDescription,
              descriptionTranslations,
              setDescriptionTranslations,
              isDescriptionChanged,
              true,
            )}

            <Box display="flex" gap={1} alignItems="center">
              <Controller
                name="category"
                control={control}
                render={({field, fieldState}) => (
                  <FieldHighlight changed={!!dirtyFields.category} flex={1}>
                    <CategorySelect
                      label="Category"
                      error={fieldState.error?.message}
                      {...field}
                      fullWidth
                    />
                  </FieldHighlight>
                )}
              />
              <Tooltip title="Open category">
                <span>
                  <IconButton
                    size="small"
                    disabled={!currentCategory}
                    onClick={() =>
                      currentCategory &&
                      navigate(
                        ROUTE__CATEGORY_EDIT.replace(':id', currentCategory.id),
                      )
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              <Controller
                name="subCategory"
                control={control}
                render={({field, fieldState}) => (
                  <FieldHighlight changed={!!dirtyFields.subCategory} flex={1}>
                    <SubCategorySelect
                      label="Sub-category"
                      categoryId={currentCategory?.id ?? ''}
                      error={fieldState.error?.message}
                      {...field}
                      fullWidth
                    />
                  </FieldHighlight>
                )}
              />
              <Tooltip title="Open sub-category">
                <span>
                  <IconButton
                    size="small"
                    disabled={!currentSubCategory || !currentCategory}
                    onClick={() =>
                      currentCategory &&
                      currentSubCategory &&
                      navigate(
                        ROUTE__SUB_CATEGORY_EDIT.replace(
                          ':categoryId',
                          currentCategory.id,
                        ).replace(':id', currentSubCategory.id),
                      )
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              <Controller
                name="topic"
                control={control}
                render={({field, fieldState}) => (
                  <FieldHighlight changed={!!dirtyFields.topic} flex={1}>
                    <TopicSelect
                      label="Topic"
                      error={fieldState.error?.message}
                      categoryId={currentCategory?.id}
                      subCategoryId={currentSubCategory?.id}
                      fullWidth
                      {...field}
                    />
                  </FieldHighlight>
                )}
              />
              <Tooltip title="Open topic">
                <span>
                  <IconButton
                    size="small"
                    disabled={!selectedTopic}
                    onClick={() =>
                      selectedTopic &&
                      navigate(
                        ROUTE__TOPIC_DETAILS.replace(
                          ':topic_id',
                          selectedTopic.topicId,
                        ),
                      )
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Controller
              name="geos"
              control={control}
              render={({field}) => {
                return (
                  <FieldHighlight changed={!!dirtyFields.geos}>
                    <ObjectSelect<GeoFilterOption>
                      label="GEO"
                      multiple
                      options={geoOptions}
                      labelProp="name"
                      valueProp="id"
                      value={field.value ?? []}
                      onChange={val => field.onChange(val ?? [])}
                      renderOption={opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {opt.logo_url && (
                              <img
                                src={opt.logo_url}
                                alt=""
                                width="auto"
                                height={20}
                                style={{flexShrink: 0}}
                              />
                            )}
                            <Typography>{opt.name || opt.id}</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      renderValue={selected => (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {(selected as string[]).map(id => {
                            const opt = geoOptions.find(o => o.id === id);
                            return (
                              <Chip
                                key={id}
                                size="small"
                                label={opt ? opt.name || opt.id : id}
                              />
                            );
                          })}
                        </Box>
                      )}
                    />
                  </FieldHighlight>
                );
              }}
            />
          </Stack>
        </Box>

        <Divider sx={{my: 3}} />

        {/* ── PARAMS ── */}
        <SectionTitle>Params</SectionTitle>
        <Stack spacing={3.5}>
          <Stack spacing={0.5}>
            <FormRow>
              <FieldHighlight changed={!!dirtyFields.startDatetime}>
                <RhfDateTimePicker
                  name="startDatetime"
                  control={control}
                  label="Start date & time"
                />
              </FieldHighlight>
              <FieldHighlight changed={!!dirtyFields.liveDatetime}>
                <RhfDateTimePicker
                  name="liveDatetime"
                  control={control}
                  label="Live date & time"
                />
              </FieldHighlight>
              <FieldHighlight changed={!!dirtyFields.endDatetime}>
                <RhfDateTimePicker
                  name="endDatetime"
                  control={control}
                  label="End date & time"
                />
              </FieldHighlight>
            </FormRow>

            <Controller
              name="autoPublishEnabled"
              control={control}
              render={({field}) => (
                <FieldHighlight changed={!!dirtyFields.autoPublishEnabled}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label={`Auto publish event at ${
                      startDatetime
                        ? formatInTz(
                            toUtcFromTz(startDatetime),
                            DATETIME_FORMAT,
                          )
                        : 'start date'
                    }`}
                  />
                </FieldHighlight>
              )}
            />
          </Stack>

          <Controller
            name="displayType"
            control={control}
            render={({field}) => (
              <FieldHighlight changed={!!dirtyFields.displayType}>
                <ObjectSelect<{value: EventDisplayType; label: string}>
                  label="Display type"
                  options={Object.values(EventDisplayType).map(t => ({
                    value: t,
                    label: formatEventDisplayType(t),
                  }))}
                  labelProp="label"
                  valueProp="value"
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: formatEventDisplayType(field.value),
                        }
                      : null
                  }
                  onChange={opt => {
                    if (!Array.isArray(opt) && opt !== null)
                      field.onChange(opt.value);
                  }}
                  sx={{maxWidth: 300}}
                />
              </FieldHighlight>
            )}
          />

          <FormRow>
            <Box display="flex" alignItems="center" gap={1}>
              <FieldHighlight changed={!!dirtyFields.yesPrice}>
                <Controller
                  name="yesPrice"
                  control={control}
                  render={({field, fieldState}) => (
                    <TextField
                      {...field}
                      label="Yes price"
                      type="number"
                      size="small"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      slotProps={{htmlInput: {min: 0, max: 0.99, step: 0.01}}}
                      sx={{width: 160}}
                    />
                  )}
                />
              </FieldHighlight>
              <Button
                variant="outlined"
                onClick={() => {
                  void handleOpenPriceDialog();
                }}
                disabled={isFetchingPrice || event.status !== EventStatus.null}
                startIcon={
                  isFetchingPrice ? <CircularProgress size={16} /> : undefined
                }
              >
                Update price
              </Button>
            </Box>
          </FormRow>
        </Stack>

        <Divider sx={{my: 3}} />

        {/* ── ADDITIONAL ── */}
        <SectionTitle>Additional</SectionTitle>
        <Stack spacing={3.5}>
          {translatedField(
            'yesText',
            'Yes text',
            currentYesText,
            yesTextTranslations,
            setYesTextTranslations,
            isYesTextChanged,
          )}
          {translatedField(
            'noText',
            'No text',
            currentNoText,
            noTextTranslations,
            setNoTextTranslations,
            isNoTextChanged,
          )}
          {translatedField(
            'overview',
            'Overview',
            currentOverview,
            overviewTranslations,
            setOverviewTranslations,
            isOverviewChanged,
            true,
          )}
          {translatedField(
            'sourceOfTruth',
            'Source of truth',
            currentSourceOfTruth,
            sourceOfTruthTranslations,
            setSourceOfTruthTranslations,
            isSourceOfTruthChanged,
            true,
          )}
          {translatedField(
            'rules',
            'Rules',
            currentRules,
            rulesTranslations,
            setRulesTranslations,
            isRulesChanged,
            true,
          )}
        </Stack>

        <Divider sx={{my: 3}} />

        <FormRow width={420}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>
          <Button
            type="submit"
            sx={{flex: 2}}
            disabled={isUpdating || isUploading}
          >
            Apply changes
          </Button>
        </FormRow>
      </form>

      <Dialog
        open={isPriceDialogOpen}
        onClose={() => setIsPriceDialogOpen(false)}
        slotProps={{paper: {sx: {width: 360, p: 2}}}}
      >
        <DialogTitle sx={{pt: 3}}>Price update</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} pt={5}>
            <TextField
              label="Suggested price"
              type="number"
              size="small"
              value={suggestedPrice}
              onChange={e => setSuggestedPrice(e.target.value)}
              fullWidth
            />
            <Box>
              <Typography color="text.secondary" sx={{fontSize: 12, mb: 0.5}}>
                From odds:
              </Typography>
              <Typography sx={{fontSize: 12, mb: 0.5, pl: 1}}>
                {priceData ? String(priceData.success) : '—'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsPriceDialogOpen(false)}
            disabled={isUpdating || isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePrice}
            disabled={isUpdating || isUploading}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
